import React, { useState, useEffect } from "react";
import Header from "../../Components/Header";
import Seo from "../../Components/SeoComponent/Seo";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import { fetchProfile } from "../../Services/ApiTestService/aadharTestService";
import { StatementAnalyzerService } from "../../Services/ApiTestService/apiTestService";
import { Button } from "../../Components/Tags/Tags";
import Loader from "../../Loader";
import { Link } from "react-router-dom";
import { useService } from "../../context/ServiceContext";
import { IoArrowBackCircleOutline } from "react-icons/io5";
import html2pdf from "html2pdf.js";
import {
  FiUpload,
  FiCheckCircle,
  FiFileText,
  FiX,
  FiFile,
  FiRefreshCw,
  FiDownload,
} from "react-icons/fi";

const StatementAnalyzer = () => {
  const [profileData, setProfileData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [requestId, setRequestId] = useState("");
  const [txnId, setTxnId] = useState("");
  const [status, setStatus] = useState("");
  const [token, setToken] = useState("");
  const [reportData, setReportData] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const { serviceName } = useService();
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setIsLoading(true);
        const response = await fetchProfile();
        setProfileData(response.data);
      } catch (error) {
        console.error("Error fetching profile:", error);
        showErrorToast("Failed to fetch profile data.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchProfileData();
  }, []);

  // File handling
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files); // FIX: was e.target.file
    setFiles((prev) => [...prev, ...selectedFiles]);
    if (selectedFiles.length > 0) {
      showSuccessToast(`${selectedFiles.length} file(s) selected`);
    }
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files); // FIX: was e.dataTransfer.file
    setFiles((prev) => [...prev, ...droppedFiles]);
    if (droppedFiles.length > 0) {
      showSuccessToast(`${droppedFiles.length} file(s) added`);
    }
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    showSuccessToast("File removed");
  };

  // Step 1: Initiate Upload
  const handleInitiateUpload = async () => {
    if (!files.length) {
      showErrorToast("Please select at least one file.");
      return;
    }

    try {
      setIsLoading(true);
      setStatus("Initiating upload...");

      const payload = {
        api_id: profileData?.user_id,
        api_key: profileData?.api_key,
        token_id: profileData?.token_id,
        method: "INITIATE_UPLOAD",
        acceptance_policy: "atLeastOneTransactionInRange",
      };

      const res = await StatementAnalyzerService(payload);
      const { code, message } = res?.status || {};

      if (code === 422) {
        showErrorToast(message || "Insufficient balance in wallet.");
        setStatus(message);
        return;
      }

      if (code === 403) {
        showErrorToast(message || "User not authorized.");
        setStatus(message);
        return;
      }

      if (code === 200) {
        const reqId = res?.data?.request_id;
        const tokenVal = res?.data?.token;
        setRequestId(reqId || "");
        setToken(tokenVal || "");
        setStatus("Upload initiated successfully!");
        setCurrentStep(2);
        showSuccessToast(message || "Upload initiated successfully.");
        await handleUploadFiles(reqId, tokenVal);
        return;
      }

      showErrorToast(message || "Failed to initiate upload.");
      setStatus("Failed to initiate upload");
    } catch (err) {
      console.error(err);
      showErrorToast("Something went wrong. Please try again.");
      setStatus("Error while initiating upload");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Upload Files
  const handleUploadFiles = async (reqId = requestId, tokenVal = token) => {
    if (!reqId || !tokenVal) {
      showErrorToast(
        "Missing request ID or token. Please initiate upload first.",
      );
      return;
    }

    if (!files.length) {
      showErrorToast("No files to upload.");
      return;
    }

    try {
      setIsLoading(true);
      setStatus("Uploading files...");
      setUploadProgress(30);

      const formData = new FormData();
      formData.append("api_id", profileData?.user_id);
      formData.append("api_key", profileData?.api_key);
      formData.append("token_id", profileData?.token_id);
      formData.append("token", tokenVal);
      formData.append("method", "UPLOAD");
      formData.append("request_id", reqId);

      files.forEach((file) => {
        formData.append("file", file);
      });

      setUploadProgress(60);
      const res = await StatementAnalyzerService(formData);
      const code = res?.status?.code;
      const msg = res?.status?.message || res?.message;
      setUploadProgress(100);

      if (code === 200) {
        setStatus("Files uploaded successfully!");
        setCurrentStep(3);
        showSuccessToast(msg || "Files uploaded successfully.");
        setTimeout(() => handleCompleteUpload(reqId), 1000);
      } else {
        showErrorToast(msg || "Failed to upload files.");
        setStatus(msg || "Failed to upload files");
      }
    } catch (err) {
      console.error(err);
      showErrorToast("Error while uploading files.");
      setStatus("Error while uploading files");
    } finally {
      setIsLoading(false);
      setTimeout(() => setUploadProgress(0), 2000);
    }
  };

  // Step 3: Complete Upload
  const handleCompleteUpload = async (reqId = requestId) => {
    if (!reqId) {
      showErrorToast("No request ID available.");
      return;
    }

    try {
      setIsLoading(true);
      setStatus("Completing upload process...");

      const payload = {
        api_id: profileData?.user_id,
        api_key: profileData?.api_key,
        token_id: profileData?.token_id,
        method: "COMPLETE_UPLOAD",
        request_id: reqId,
      };

      const res = await StatementAnalyzerService(payload);
      const code = res?.status?.code;
      const msg = res?.status?.message || res?.message;

      if (code === 200) {
        setStatus("Upload completed successfully!");
        setCurrentStep(4);
        showSuccessToast(msg || "Upload completed. Analysis in progress.");
      } else {
        showErrorToast(msg || "Failed to complete upload.");
        setStatus("Failed to complete upload");
      }
    } catch (err) {
      console.error(err);
      showErrorToast("Error while completing upload.");
      setStatus("Error while completing upload");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 4: Check Status
  const handleCheckStatus = async () => {
    if (!requestId) {
      showErrorToast("No request ID available. Please upload files first.");
      return;
    }

    try {
      setIsLoading(true);
      setStatus("Checking status...");

      const payload = {
        api_id: profileData?.user_id,
        api_key: profileData?.api_key,
        token_id: profileData?.token_id,
        method: "CHECK_STATUS",
        request_id: requestId,
      };

      const res = await StatementAnalyzerService(payload);
      const code = res?.status?.code;
      const msg = res?.status?.message || res?.message;

      if (code === 200) {
        const statusInfo = res?.data?.status || "Processing";
        setTxnId(res?.data?.txn_id || "");
        setStatus(`Status: ${statusInfo}`);
        showSuccessToast(msg || `Current status: ${statusInfo}`);
      } else {
        showErrorToast(msg || "Failed to check status.");
        setStatus("Failed to check status");
      }
    } catch (err) {
      console.error(err);
      showErrorToast("Error while checking status.");
      setStatus("Error while checking status");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 5: Get Report
  const handleGetReport = async (
    reportType = "json",
    reportSubtype = "type1",
  ) => {
    if (!txnId) {
      showErrorToast(
        "No transaction ID available. Please complete the upload first.",
      );
      return;
    }

    try {
      setIsLoading(true);
      setStatus("Retrieving report...");

      const payload = {
        api_id: profileData?.user_id,
        api_key: profileData?.api_key,
        token_id: profileData?.token_id,
        method: "RETRIEVE_STATEMENT",
        txn_id: txnId,
        report_type: reportType,
        report_subtype: reportSubtype,
      };

      const res = await StatementAnalyzerService(payload);
      const code = res?.status?.code;
      const msg = res?.status?.message || res?.message;

      if (code === 200) {
        setReportData(res?.data);
        setStatus("Report retrieved successfully!");
        setCurrentStep(5);
        showSuccessToast(msg || "Report retrieved successfully.");
      } else {
        showErrorToast(msg || "Failed to retrieve report.");
        setStatus("Failed to retrieve report");
      }
    } catch (err) {
      console.error(err);
      showErrorToast("Error while retrieving report.");
      setStatus("Error while retrieving report");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFiles([]);
    setRequestId("");
    setTxnId("");
    setStatus("");
    setReportData(null);
    setCurrentStep(1);
    setUploadProgress(0);
    showSuccessToast("Reset successful. Ready for new upload.");
  };

  const downloadReport = () => {
    const dataStr = JSON.stringify(reportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `statement-report-${txnId}.json`;
    link.click();
  };

  const steps = [
    { num: 1, label: "Select Files", icon: FiFile },
    { num: 2, label: "Upload", icon: FiUpload },
    { num: 3, label: "Process", icon: FiRefreshCw },
    { num: 4, label: "Status", icon: FiCheckCircle },
    { num: 5, label: "Report", icon: FiFileText },
  ];

  const handleDownloadPDF = () => {
    setIsDownloading(true);

    setTimeout(() => {
      const element = document.getElementById("bank-statement-report");
      const name = reportData?.customer_info?.name || "Customer";

      if (element) {
        // Clone the element
        const clonedElement = element.cloneNode(true);

        // Reset styles for PDF
        clonedElement.style.margin = "0";
        clonedElement.style.padding = "0";
        clonedElement.style.width = "100%";
        clonedElement.style.maxWidth = "100%";
        clonedElement.style.overflow = "visible";

        // Create temporary container
        const tempDiv = document.createElement("div");
        tempDiv.style.position = "absolute";
        tempDiv.style.top = "-9999px";
        tempDiv.style.left = "-9999px";
        tempDiv.style.width = "210mm"; // A4 width
        tempDiv.style.backgroundColor = "white";
        tempDiv.appendChild(clonedElement);
        document.body.appendChild(tempDiv);

        // Generate PDF with optimized settings
        html2pdf()
          .from(clonedElement)
          .set({
            margin: [0.3, 0.3, 0.3, 0.3],
            filename: `Bank_Statement_Report_${name.replace(/\s+/g, "_")}.pdf`,
            image: { type: "jpeg", quality: 0.95 },
            html2canvas: {
              scale: 2,
              useCORS: true,
              letterRendering: true,
              backgroundColor: "#ffffff",
              windowWidth: 1024,
              scrollY: 0,
              scrollX: 0,
            },
            jsPDF: {
              unit: "mm",
              format: "a4",
              orientation: "portrait",
              compress: true,
            },
            pagebreak: {
              mode: ["avoid-all", "css", "legacy"],
              before: ".break-before",
              after: ".break-after",
              avoid: ".break-inside-avoid",
            },
          })
          .save()
          .then(() => {
            document.body.removeChild(tempDiv);
            setIsDownloading(false);
          })
          .catch((error) => {
            console.error("PDF generation error:", error);
            showErrorToast("Failed to generate PDF");
            if (document.body.contains(tempDiv)) {
              document.body.removeChild(tempDiv);
            }
            setIsDownloading(false);
          });
      } else {
        showErrorToast("No data found to download");
        setIsDownloading(false);
      }
    }, 100);
  };

  return (
    <>
      <Seo />
      {isLoading && <Loader />}
      <Header heading={serviceName} />

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-3 px-2 sm:px-3 lg:px-4">
        <div className="max-w-7xl mx-auto">
          {/* Progress Stepper */}
          <div className="bg-white rounded-xl shadow-lg p-2 mb-1">
            <div className="flex items-center justify-between mb-2">
              {steps.map((step, index) => (
                <React.Fragment key={step.num}>
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                        currentStep >= step.num
                          ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg"
                          : "bg-gray-200 text-gray-500"
                      }`}
                    >
                      <step.icon size={20} />
                    </div>
                    <span
                      className={`text-xs mt-2 font-medium ${
                        currentStep >= step.num
                          ? "text-blue-600"
                          : "text-gray-500"
                      }`}
                    >
                      {step.label}
                    </span>
                  </div>
                  {index < 4 && (
                    <div
                      className={`flex-1 h-1 mx-2 transition-all duration-300 ${
                        currentStep > step.num
                          ? "bg-gradient-to-r from-blue-600 to-purple-600"
                          : "bg-gray-200"
                      }`}
                    ></div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
            {/* File Upload */}
            <div className="lg:col-span-2 space-y-3">
              <div className="bg-white rounded-xl shadow-lg p-3">
                <h2 className="text-xl font-bold text-gray-800 mb-2 flex items-center gap-2">
                  <FiUpload className="text-blue-600" />
                  Upload Bank Statements
                </h2>

                {/* Drag & Drop Area */}
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                  }}
                  onDrop={handleDrop}
                  className={`border-3 border-dashed rounded-xl p-3 text-center transition-all duration-300 cursor-pointer ${
                    isDragging
                      ? "border-blue-600 bg-blue-50 scale-105"
                      : "border-gray-300 hover:border-blue-400 hover:bg-gray-50"
                  }`}
                >
                  <label
                    htmlFor="fileInput"
                    className="flex flex-col items-center cursor-pointer"
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mb-2">
                      <FiUpload size={36} className="text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">
                      Drop your files here, or browse
                    </h3>
                    <p className="text-sm text-gray-500 mb-2">
                      Supports PDF bank statements
                    </p>
                    <input
                      id="fileInput"
                      type="file"
                      multiple
                      onChange={handleFileChange}
                      className="hidden"
                      accept=".pdf,.xlsx,.xls,.csv"
                    />
                  </label>
                </div>

                {/* Upload Progress */}
                {uploadProgress > 0 && (
                  <div className="mt-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        Uploading...
                      </span>
                      <span className="text-sm font-medium text-blue-600">
                        {uploadProgress}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-blue-600 to-purple-600 h-3 rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Selected Files */}
                {files.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">
                      Selected Files ({files.length})
                    </h3>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {files.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-purple-50 p-3 rounded-lg border border-blue-200"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <FiFile
                              className="text-blue-600 flex-shrink-0"
                              size={20}
                            />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-800 truncate">
                                {file.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {(file.size / 1024).toFixed(2)} KB
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => removeFile(index)}
                            className="ml-2 text-red-500 hover:text-red-700 transition-colors p-1 hover:bg-red-100 rounded-full flex-shrink-0"
                          >
                            <FiX size={18} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="bg-white rounded-xl shadow-lg p-2">
                <h2 className="text-xl font-bold text-gray-800 mb-2">
                  Actions
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Button
                    type="button"
                    text={
                      <span className="flex items-center justify-center gap-2">
                        <FiUpload size={18} />
                        Start Upload
                      </span>
                    }
                    onClick={handleInitiateUpload}
                    disabled={files.length === 0}
                    className={`px-3 py-2 rounded-lg font-semibold transition-all duration-300 shadow-md ${
                      files.length === 0
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 transform hover:scale-105"
                    }`}
                  />

                  <Button
                    type="button"
                    text={
                      <span className="flex items-center justify-center gap-2">
                        <FiRefreshCw size={18} />
                        Check Status
                      </span>
                    }
                    onClick={handleCheckStatus}
                    disabled={!requestId}
                    className={`px-3 py-2 rounded-lg font-semibold transition-all duration-300 shadow-md ${
                      !requestId
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-gradient-to-r from-yellow-500 to-yellow-600 text-white hover:from-yellow-600 hover:to-yellow-700 transform hover:scale-105"
                    }`}
                  />

                  <Button
                    type="button"
                    text={
                      <span className="flex items-center justify-center gap-2">
                        <FiDownload size={18} />
                        Get Report
                      </span>
                    }
                    onClick={() => handleGetReport("json", "type1")}
                    disabled={!txnId}
                    className={`px-3 py-2 rounded-lg font-semibold transition-all duration-300 shadow-md ${
                      !txnId
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800 transform hover:scale-105"
                    }`}
                  />

                  <Button
                    type="button"
                    text={
                      <span className="flex items-center justify-center gap-2">
                        <FiRefreshCw size={18} />
                        Reset
                      </span>
                    }
                    onClick={handleReset}
                    className="px-3 py-2 rounded-lg font-semibold transition-all duration-300 shadow-md bg-gradient-to-r from-gray-600 to-gray-700 text-white hover:from-gray-700 hover:to-gray-800 transform hover:scale-105 col-span-2 sm:col-span-1"
                  />
                </div>
              </div>
            </div>

            {/* Status Panel */}
            <div className="space-y-2">
              <div className="bg-white rounded-xl shadow-lg p-2">
                <h2 className="text-xl font-bold text-gray-800 mb-2 flex items-center gap-2">
                  <FiCheckCircle className="text-green-600" />
                  Current Status
                </h2>
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-2 border-l-4 border-blue-600">
                  <p className="text-sm font-medium text-gray-800">
                    {status || "Ready to upload files"}
                  </p>
                </div>

                <div className="mt-6 space-y-3">
                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-600">
                      Request ID:
                    </span>
                    <span className="text-sm font-semibold text-gray-800 truncate ml-2 max-w-[150px]">
                      {requestId || "N/A"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-600">
                      Transaction ID:
                    </span>
                    <span className="text-sm font-semibold text-gray-800 truncate ml-2 max-w-[150px]">
                      {txnId || "N/A"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-600">
                      Files Selected:
                    </span>
                    <span className="text-sm font-semibold text-gray-800">
                      {files.length}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl shadow-lg p-2 text-white">
                <h2 className="text-xl font-bold mb-4">How It Works</h2>
                <ol className="space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="font-bold">1.</span>
                    <span>
                      Upload your bank statement files (PDF, Excel, CSV)
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold">2.</span>
                    <span>Click "Start Upload" to initiate the process</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold">3.</span>
                    <span>System will automatically process your files</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold">4.</span>
                    <span>Check status to monitor the analysis progress</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold">5.</span>
                    <span>Download the comprehensive analysis report</span>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full flex">
        <div className="w-1/2">
          {/* Report Display */}
          {reportData && (
            <div className="bg-white rounded-xl shadow-lg p-2">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <FiFileText className="text-green-600" />
                  Analysis Report
                </h2>
                <Button
                  type="button"
                  text={
                    <span className="flex items-center gap-2">
                      <FiDownload size={16} />
                      Download JSON
                    </span>
                  }
                  onClick={downloadReport}
                  className="bg-gradient-to-r from-green-600 to-green-700 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:from-green-700 hover:to-green-800 transition-all duration-300 shadow-md"
                />
              </div>
              <div className="bg-gray-50 rounded-lg p-2 overflow-auto">
                <pre className="text-xs text-gray-800 whitespace-pre-wrap break-words">
                  {JSON.stringify(reportData, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>

        <div className="w-1/2">
          {reportData && (
            <div className="bg-white p-3 text-xs" id="bank-statement-report">
              {/* Header Section */}
              <div className="border border-gray-300 p-3 mb-3">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="bg-green-700 p-1.5 rounded">
                      <FiFileText className="text-white text-sm" />
                    </div>
                    <div>
                      <h2 className="text-base font-bold text-gray-900">
                        Bank Statement Analysis Report
                      </h2>
                      <p className="text-xs text-gray-600 mt-0.5">
                        Transaction ID: {reportData?.txn_id}
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    text={
                      <span className="flex items-center gap-1.5">
                        <FiDownload size={14} />
                        Download
                      </span>
                    }
                    onClick={downloadReport}
                    className="bg-green-700 text-white px-3 py-1.5 rounded text-xs font-semibold hover:bg-green-800 print:hidden"
                  />
                </div>

                {/* Statement Period Info */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <div className="bg-blue-50 p-2 rounded border border-blue-200">
                    <p className="text-xs text-blue-700 font-semibold uppercase mb-0.5">
                      Statement Period
                    </p>
                    <p className="text-xs font-bold text-blue-900 leading-tight">
                      {reportData?.statement_start_date} to{" "}
                      {reportData?.statement_end_date}
                    </p>
                  </div>
                  <div className="bg-purple-50 p-2 rounded border border-purple-200">
                    <p className="text-xs text-purple-700 font-semibold uppercase mb-0.5">
                      Duration
                    </p>
                    <p className="text-xs font-bold text-purple-900">
                      {reportData?.duration_in_month} Months
                    </p>
                  </div>
                  <div className="bg-amber-50 p-2 rounded border border-amber-200">
                    <p className="text-xs text-amber-700 font-semibold uppercase mb-0.5">
                      Request ID
                    </p>
                    <p className="text-xs font-bold text-amber-900">
                      {reportData?.request_id}
                    </p>
                  </div>
                  <div className="bg-green-50 p-2 rounded border border-green-200">
                    <p className="text-xs text-green-700 font-semibold uppercase mb-0.5">
                      Status
                    </p>
                    <p className="text-xs font-bold text-green-900 uppercase">
                      {reportData?.status?.type}
                    </p>
                  </div>
                </div>
              </div>

              {/* Customer Information */}
              <div className="border border-gray-300 p-3 mb-3">
                <h3 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-1.5">
                  <span className="w-0.5 h-4 bg-green-700 rounded-full"></span>
                  Customer Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                      Account Holder Name
                    </span>
                    <span className="text-xs font-bold text-gray-900">
                      {reportData?.customer_info?.name || "N/A"}
                    </span>
                  </div>

                  <div>
                    <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                      Email
                    </span>
                    <span className="text-xs text-gray-900">
                      {reportData?.customer_info?.email || "N/A"}
                    </span>
                  </div>

                  <div>
                    <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                      Contact Number
                    </span>
                    <span className="text-xs text-gray-900">
                      {reportData?.customer_info?.contact_number || "N/A"}
                    </span>
                  </div>

                  <div>
                    <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                      PAN
                    </span>
                    <span className="text-xs text-gray-900">
                      {reportData?.customer_info?.pan || "N/A"}
                    </span>
                  </div>

                  <div>
                    <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                      Address
                    </span>
                    <span className="text-xs text-gray-900 leading-tight">
                      {reportData?.customer_info?.address || "N/A"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Account Details */}
                {reportData?.accounts?.map((account, idx) => (
                      <div
                        key={idx}
                        className="border border-gray-300 p-2 mb-2 page-break-before"
                      >
                        <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <span className="w-1 h-5 bg-blue-700 rounded-full"></span>
                          Account Details
                        </h3>

                        <div className="grid grid-cols-3 gap-1 mb-2">
                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                              Institution ID
                            </span>
                            <span className="text-xs text-gray-900">
                              {account.institution_id || "N/A"}
                            </span>
                          </div>

                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                              Account Opening Date
                            </span>
                            <span className="text-xs text-gray-900">
                              {account.account_opening_date || "N/A"}
                            </span>
                          </div>

                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                              Bank Name
                            </span>
                            <span className="text-xs font-bold text-gray-900">
                              {account.bank}
                            </span>
                          </div>
                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                              Account Number
                            </span>
                            <span className="text-xs font-bold text-gray-900">
                              {account.account_number}
                            </span>
                          </div>
                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                              Account Type
                            </span>
                            <span className="text-xs font-bold text-gray-900">
                              {account.account_type}
                            </span>
                          </div>

                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                              Account Description
                            </span>
                            <span className="text-xs font-bold text-gray-900">
                              {account.account_description}
                            </span>
                          </div>

                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                              Branch
                            </span>
                            <span className="text-xs text-gray-900">
                              {account.location}
                            </span>
                          </div>
                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                              IFSC Code
                            </span>
                            <span className="text-xs font-mono text-gray-900">
                              {account.ifsc_code}
                            </span>
                          </div>
                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                              MICR Code
                            </span>
                            <span className="text-xs font-mono text-gray-900">
                              {account.micr_code}
                            </span>
                          </div>

                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                              OD Limit
                            </span>
                            <span className="text-xs text-gray-900">
                              {account.od_limit || "N/A"}
                            </span>
                          </div>

                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                              Drawing Power
                            </span>
                            <span className="text-xs text-gray-900">
                              {account.drawing_power || "N/A"}
                            </span>
                          </div>

                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                              Transaction Start Date
                            </span>
                            <span className="text-xs text-gray-900">
                              {account.transaction_start_date || "N/A"}
                            </span>
                          </div>

                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-600 uppercase font-semibold block mb-1">
                              Transaction End Date
                            </span>
                            <span className="text-xs text-gray-900">
                              {account.transaction_end_date || "N/A"}
                            </span>
                          </div>
                        </div>

                        {/* Transactions Table */}
                        <div className="mt-4">
                          <h4 className="text-xs font-bold text-gray-900 mb-2 flex items-center gap-2">
                            <span className="w-0.5 h-4 bg-indigo-700 rounded-full"></span>
                            Transaction History (
                            {account.transactions?.length || 0})
                          </h4>

                          <div className="overflow-x-auto border border-gray-300 rounded">
                            <table className="w-full text-xs border-collapse">
                              <thead>
                                <tr className="bg-gray-800 text-white">
                                  <th className="px-2 py-2 text-left font-semibold border-r border-gray-600">
                                    Date
                                  </th>
                                  <th className="px-2 py-2 text-left font-semibold border-r border-gray-600">
                                    Description
                                  </th>
                                  <th className="px-2 py-2 text-right font-semibold border-r border-gray-600">
                                    Debit (₹)
                                  </th>
                                  <th className="px-2 py-2 text-right font-semibold border-r border-gray-600">
                                    Credit (₹)
                                  </th>
                                  <th className="px-2 py-2 text-right font-semibold border-r border-gray-600">
                                    Balance (₹)
                                  </th>
                                  <th className="px-2 py-2 text-center font-semibold">
                                    Status
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {account.transactions?.map((txn, txnIdx) => (
                                  <tr
                                    key={txnIdx}
                                    className={`${
                                      txnIdx % 2 === 0
                                        ? "bg-white"
                                        : "bg-gray-50"
                                    } border-b border-gray-200 page-break-avoid`}
                                  >
                                    <td className="px-2 py-2 whitespace-nowrap font-medium text-gray-900 border-r border-gray-200">
                                      {txn.date}
                                    </td>
                                    <td className="px-2 py-2 text-gray-700 max-w-xs border-r border-gray-200">
                                      <div
                                        className="truncate text-xs leading-tight"
                                        title={txn.narration}
                                      >
                                        {txn.narration}
                                      </div>
                                    </td>
                                    <td className="px-2 py-2 text-right font-semibold border-r border-gray-200">
                                      {parseFloat(txn.amount) < 0 ? (
                                        <span className="text-red-700">
                                          {Math.abs(
                                            parseFloat(txn.amount),
                                          ).toFixed(2)}
                                        </span>
                                      ) : (
                                        <span className="text-gray-400">-</span>
                                      )}
                                    </td>
                                    <td className="px-2 py-2 text-right font-semibold border-r border-gray-200">
                                      {parseFloat(txn.amount) > 0 ? (
                                        <span className="text-green-700">
                                          {parseFloat(txn.amount).toFixed(2)}
                                        </span>
                                      ) : (
                                        <span className="text-gray-400">-</span>
                                      )}
                                    </td>
                                    <td className="px-2 py-2 text-right font-bold text-gray-900 border-r border-gray-200">
                                      {parseFloat(txn.balance).toFixed(2)}
                                    </td>
                                    <td className="px-2 py-2 text-center">
                                      <span
                                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                                          txn.tamper_flag === "GREEN"
                                            ? "bg-green-100 text-green-800"
                                            : "bg-red-100 text-red-800"
                                        }`}
                                      >
                                        {txn.tamper_flag === "GREEN"
                                          ? "✓"
                                          : "✗"}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Tamper Detection Section */}
                    {reportData?.tamper_detection_details && (
                      <div className="border border-gray-300 p-4 mb-4 page-break-avoid">
                        <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <span className="w-1 h-5 bg-red-700 rounded-full"></span>
                          Security & Fraud Analysis
                        </h3>

                        {reportData.tamper_detection_details.map(
                          (detail, idx) => (
                            <div
                              key={idx}
                              className="grid grid-cols-1 sm:grid-cols-3 gap-3"
                            >
                              <div className="bg-green-50 p-3 rounded border border-green-200">
                                <span className="text-xs text-green-700 uppercase font-semibold block mb-1">
                                  PDF Confidence Score
                                </span>
                                <span className="text-lg font-bold text-green-900">
                                  {detail.original_pdf_confidence_score}%
                                </span>
                              </div>
                              <div className="bg-blue-50 p-3 rounded border border-blue-200">
                                <span className="text-xs text-blue-700 uppercase font-semibold block mb-1">
                                  Fraud Analysis Score
                                </span>
                                <span className="text-lg font-bold text-blue-900">
                                  {detail.additional_fraud_analysis_score}%
                                </span>
                              </div>
                              <div className="bg-purple-50 p-3 rounded border border-purple-200">
                                <span className="text-xs text-purple-700 uppercase font-semibold block mb-1">
                                  Tampering Detected
                                </span>
                                <span className="text-lg font-bold text-purple-900">
                                  {detail.txn_amt_bal_tampering_detected}
                                </span>
                              </div>

                              <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                                <span className="text-xs text-yellow-700 uppercase font-semibold block mb-1">
                                  Standard Bank Font
                                </span>
                                <span className="text-lg font-bold text-yellow-900">
                                  {detail.is_standard_bank_font === 1
                                    ? "Yes"
                                    : "No"}
                                </span>
                              </div>

                              <div className="bg-orange-50 p-3 rounded border border-orange-200">
                                <span className="text-xs text-orange-700 uppercase font-semibold block mb-1">
                                  Standard PDF Font
                                </span>
                                <span className="text-lg font-bold text-orange-900">
                                  {detail.is_standard_pdf_font === 1
                                    ? "Yes"
                                    : "No"}
                                </span>
                              </div>

                              <div className="bg-indigo-50 p-3 rounded border border-indigo-200">
                                <span className="text-xs text-indigo-700 uppercase font-semibold block mb-1">
                                  Tampered Transactions
                                </span>
                                <span className="text-lg font-bold text-indigo-900">
                                  {detail.specific_amt_bal_tampered_txn_detected ||
                                    "N/A"}
                                </span>
                              </div>

                              <div className="bg-gray-50 p-3 rounded border border-gray-200">
                                <span className="text-xs text-gray-700 uppercase font-semibold block mb-1">
                                  Overall Balance Difference
                                </span>
                                <span className="text-lg font-bold text-gray-900">
                                  {detail.overall_balance_difference || "0.0"}
                                </span>
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default StatementAnalyzer;
